import { motion } from 'motion/react';
import { cx } from '@/lib/cx';

export interface ProgressCounterProps {
  /** What is being counted, e.g. "Answers". */
  label: string;
  value: number;
  total: number;
  /** Noun used in the accessible value text, e.g. "answers complete". */
  unit?: string;
  /** Render as discrete segments when total is small enough (default ≤ 24). */
  maxSegments?: number;
  size?: 'sm' | 'md';
  /** Shown instead of the bar when total is 0. */
  emptyText?: string;
  /** Mark that some items were waived (text marker, not colour). */
  waived?: boolean;
  className?: string;
  hideLabel?: boolean;
}

/**
 * One honest counter: "Answers 18/20". Never collapses several dimensions into a single
 * percentage. Small totals render as discrete segments so each item is visibly accounted for.
 */
export function ProgressCounter({ label, value, total, unit, maxSegments = 24, size = 'md', emptyText = 'None required', waived, className, hideLabel }: ProgressCounterProps) {
  const safeTotal = Math.max(0, total);
  const safeValue = Math.min(Math.max(0, value), safeTotal);
  const complete = safeTotal > 0 && safeValue === safeTotal;
  const tone = safeTotal === 0 ? 'muted' : complete ? 'success' : safeValue === 0 ? 'neutral' : 'progress';
  const valueText = safeTotal === 0 ? `${label}: ${emptyText}` : `${label}: ${safeValue} of ${safeTotal}${unit ? ` ${unit}` : ''}${waived ? ', includes waived items' : ''}`;
  const segmented = safeTotal > 0 && safeTotal <= maxSegments;

  return (
    <div className={cx('progress-counter', `pc-${size}`, `tone-${tone}`, className)}>
      <div className="pc-head">
        {!hideLabel && <span className="pc-label">{label}</span>}
        <span className="pc-value tabular" aria-hidden>
          {safeTotal === 0 ? (
            <span className="pc-empty">{emptyText}</span>
          ) : (
            <>
              <strong>{safeValue}</strong>
              <span className="pc-sep">/</span>
              {safeTotal}
            </>
          )}
          {waived && <span className="pc-waived">incl. waived</span>}
        </span>
      </div>
      {safeTotal > 0 && (
        <div className="pc-track" role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={safeTotal} aria-valuenow={safeValue} aria-valuetext={valueText}>
          {segmented ? (
            Array.from({ length: safeTotal }, (_, i) => <span key={i} className={cx('pc-seg', i < safeValue && 'is-filled')} />)
          ) : (
            <motion.span className="pc-fill" initial={false} animate={{ width: `${(safeValue / safeTotal) * 100}%` }} transition={{ duration: 0.4, ease: [0.2, 0.8, 0.2, 1] }} />
          )}
        </div>
      )}
      {safeTotal === 0 && <span className="sr-only">{valueText}</span>}
    </div>
  );
}
